"use client"

import Image from "next/image"
import { usePathname } from "next/navigation"
import { navigateUrl } from "@/utils/client-request"
import { ChevronLeft } from "lucide-react"
import Logo from "@/assets/logo-icon-black.svg"
import Bubble1 from "@/assets/auth-bubble-1.svg"
import Bubble2 from "@/assets/auth-bubble-2.svg"
import Bubble3 from "@/assets/auth-bubble-3.svg"
import Bubble4 from "@/assets/auth-bubble-4.svg"

const BUBBLES = [Bubble1, Bubble2, Bubble3, Bubble4]

function ScrollRow({ reverse, offset = 0 }: { reverse?: boolean; offset?: number }) {
  const items = Array.from({ length: 13 }, (_, i) => (
    <Image key={i} src={BUBBLES[(i + offset) % 4]} alt="" className="size-18 shrink-0 rounded-full" />
  ))

  return (
    <div className="flex items-center gap-5">
      <div className={`relative flex items-center gap-5 animate-infinite-scroll ${reverse ? "[animation-direction:reverse]" : ""}`}>
        {items}
      </div>
      <div className={`relative flex items-center gap-5 animate-infinite-scroll ${reverse ? "[animation-direction:reverse]" : ""}`}>
        {items}
      </div>
    </div>
  )
}

export default function AuthLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || ''
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
      <div className="hidden lg:flex lg:col-span-4 bg-zinc-100 text-foreground flex-col justify-between overflow-hidden border-r border-black/5">
        <div className="grow basis-0 p-4 lg:p-10 flex flex-col gap-48">
          <a href="https://shop.mn" target="_blank" rel="noopener noreferrer">
            <Image src={Logo} alt="Shop.mn" className="h-10 w-auto" />
          </a>
          <div className="flex flex-col gap-10 font-gilroy">
            <h1 className="text-2xl font-medium leading-8 tracking-[-0.6%] text-pretty">
              Анхны хиймэл оюун ухаант цахим дэлгүүрээ нэг минутанд нээж, бизнесээ эхлүүлээрэй.
            </h1>
            <a href="https://shop.mn" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-zinc-200 px-4 py-1.5 rounded-md text-base font-medium w-fit hover:bg-zinc-300 transition">
              Дэлгэрэнгүй
            </a>
          </div>
        </div>
        <div className="w-full grow basis-0">
          <div className="relative size-full overflow-hidden">
            <div className="absolute bottom-0 left-1/2 size-[80px] -translate-x-1/2 translate-y-1/2 scale-x-[1.6] mix-blend-overlay">
              <div className="absolute -inset-16 mix-blend-overlay blur-[50px] saturate-[2] bg-[conic-gradient(from_90deg,#F00_5deg,#EAB308_63deg,#5CFF80_115deg,#1E00FF_170deg,#855AFC_220deg,#3A8BFD_286deg,#F00_360deg)]" />
              <div className="absolute -inset-16 mix-blend-overlay blur-[50px] saturate-[2] bg-[conic-gradient(from_90deg,#F00_5deg,#EAB308_63deg,#5CFF80_115deg,#1E00FF_170deg,#855AFC_220deg,#3A8BFD_286deg,#F00_360deg)]" />
            </div>
            <div className="absolute bottom-0 left-1/2 size-[80px] -translate-x-1/2 translate-y-1/2 scale-x-[1.6] opacity-15">
              <div className="absolute -inset-16 mix-blend-overlay blur-[50px] saturate-[2] bg-[conic-gradient(from_90deg,#F00_5deg,#EAB308_63deg,#5CFF80_115deg,#1E00FF_170deg,#855AFC_220deg,#3A8BFD_286deg,#F00_360deg)]" />
            </div>
            <div className="relative isolate size-full">
              <div className="relative size-full [mask-composite:intersect] [mask-image:linear-gradient(#000f_50%,#0006),linear-gradient(90deg,#000f_50%,#000a)]">
                <div className="translate-y-[30%] skew-y-[-16deg]">
                  <div className="flex flex-col gap-7">
                    <ScrollRow offset={0} reverse />
                    <ScrollRow offset={1} />
                    <ScrollRow offset={2} reverse />
                    <ScrollRow offset={3} />
                  </div>
                </div>
              </div>
              <div className="absolute inset-0">
                <div className="absolute inset-y-0 right-0 w-1/4">
                  <div className="pointer-events-none absolute inset-0" style={{ transformOrigin: "right" }}>
                    <div className="relative z-0 size-full" style={{ background: "linear-gradient(to left, rgb(from transparent r g b / alpha) 0%, rgb(from transparent r g b / 0%) 100%)" }}>
                      {[12.5, 25, 37.5, 50, 62.5, 75, 87.5].map((stop, i) => (
                        <div key={i} className="absolute inset-0" style={{
                          zIndex: i + 1,
                          mask: i === 0
                            ? `linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0) ${stop}%)`
                            : `linear-gradient(to left, rgba(0,0,0,0) ${stop - 12.5}%, rgba(0,0,0,1) ${stop}%, rgba(0,0,0,1) ${stop + 12.5}%, rgba(0,0,0,0) ${stop + 25}%)`,
                          backdropFilter: `blur(${(6 / Math.pow(1.42, i)).toFixed(1)}px)`,
                          WebkitBackdropFilter: `blur(${(6 / Math.pow(1.42, i)).toFixed(1)}px)`,
                        }} />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="absolute inset-x-0 bottom-0 h-1/4">
                  <div className="pointer-events-none absolute inset-0" style={{ transformOrigin: "bottom" }}>
                    <div className="relative z-0 size-full" style={{ background: "linear-gradient(to top, rgb(from transparent r g b / alpha) 0%, rgb(from transparent r g b / 0%) 100%)" }}>
                      {[12.5, 25, 37.5, 50, 62.5, 75, 87.5].map((stop, i) => (
                        <div key={i} className="absolute inset-0" style={{
                          zIndex: i + 1,
                          mask: i === 0
                            ? `linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) ${stop}%)`
                            : `linear-gradient(to top, rgba(0,0,0,0) ${stop - 12.5}%, rgba(0,0,0,1) ${stop}%, rgba(0,0,0,1) ${stop + 12.5}%, rgba(0,0,0,0) ${stop + 25}%)`,
                          backdropFilter: `blur(${(6 / Math.pow(1.42, i)).toFixed(1)}px)`,
                          WebkitBackdropFilter: `blur(${(6 / Math.pow(1.42, i)).toFixed(1)}px)`,
                        }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="relative col-span-1 lg:col-span-8 flex items-center justify-center p-6">
        <a href={navigateUrl('/', pathname)} className="absolute top-6 left-6 z-10 inline-flex items-center gap-1 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition font-gilroy">
          <ChevronLeft className="size-4" />
          Буцах
        </a>
        <div className="absolute inset-0 isolate overflow-hidden bg-white">
          <div className="absolute inset-y-0 left-1/2 w-300 -translate-x-1/2 [mask-composite:intersect] [mask-image:linear-gradient(black,transparent_320px),linear-gradient(90deg,transparent,black_5%,black_95%,transparent)]">
            <svg className="pointer-events-none absolute inset-0 text-neutral-200" width="100%" height="100%">
              <defs>
                <pattern id="grid-auth" x="-0.25" y="-1" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 0 0 60" fill="transparent" stroke="currentColor" strokeWidth="1" />
                </pattern>
              </defs>
              <rect fill="url(#grid-auth)" width="100%" height="100%" />
            </svg>
          </div>
          <div className="absolute left-1/2 top-6 size-20 -translate-x-1/2 -translate-y-1/2 scale-x-[1.6] mix-blend-overlay">
            <div className="absolute -inset-16 mix-blend-overlay blur-[50px] saturate-[2] bg-[conic-gradient(from_90deg,#F00_5deg,#EAB308_63deg,#5CFF80_115deg,#1E00FF_170deg,#855AFC_220deg,#3A8BFD_286deg,#F00_360deg)]" />
            <div className="absolute -inset-16 mix-blend-overlay blur-[50px] saturate-[2] bg-[conic-gradient(from_90deg,#F00_5deg,#EAB308_63deg,#5CFF80_115deg,#1E00FF_170deg,#855AFC_220deg,#3A8BFD_286deg,#F00_360deg)]" />
          </div>
          <div className="absolute left-1/2 top-6 size-20 -translate-x-1/2 -translate-y-1/2 scale-x-[1.6] opacity-10">
            <div className="absolute -inset-16 mix-blend-overlay blur-[50px] saturate-[2] bg-[conic-gradient(from_90deg,#F00_5deg,#EAB308_63deg,#5CFF80_115deg,#1E00FF_170deg,#855AFC_220deg,#3A8BFD_286deg,#F00_360deg)]" />
          </div>
        </div>
        <div className="relative w-full max-w-md px-4 font-gilroy">
          {children}
        </div>
      </div>
    </div>
  )
}
